import { useEffect, useRef, useState } from "react";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";
import { Button } from "bootstrap";

export default function SubscribeModal({ isOpen, onClose, subscriptionData }) {
  const modalRef = useRef();
  const cancelModalRef = useRef();
  const [config, setConfig] = useState(null);
  const [toast, setToast] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [subscription, setSubscription] = useState(
    subscriptionData?.subscription || subscriptionData || null
  );
  console.log("Subscription data received:", subscriptionData);
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  console.log("SubscriptionModal rendered. isOpen:", subscription);
  // Close correct modal on outside click (top-most wins)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // If cancel modal is open, only close it when clicking outside it
      if (showCancelModal) {
        if (
          cancelModalRef.current &&
          !cancelModalRef.current.contains(e.target)
        ) {
          setShowCancelModal(false);
        }
        return; // do not bubble to close the parent modal
      }
      // Otherwise, close the parent subscription modal
      if (isOpen && modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, showCancelModal]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // reset nested modal state on open
      setShowCancelModal(false);
      setSelectedReason("");
      setOtherReason("");
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto"; // cleanup
    };
  }, [isOpen]);

  // Sync subscription when prop changes (handles both shapes)
  useEffect(() => {
    const normalized =
      subscriptionData?.subscription || subscriptionData || null;
    setSubscription(normalized);
  }, [subscriptionData]);

  const verifyProfile = async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.PARTNER, {
        method: "PUT",
        body: JSON.stringify({ isVerified: true }),
      });
      if (!res.ok) throw new Error("Profile verification failed");
      return true;
    } catch (err) {
      console.error("Error verifying profile:", err);
      return false;
    }
  };

  const cancelSubscription = async (reason) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.CANCEL_SUBSCRIPTION, {
        method: "PUT",
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error("Profile verification failed");
      return true;
    } catch (err) {
      console.error("Error verifying profile:", err);
      return false;
    }
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const configresponse = await fetchWithAuth(API_ENDPOINTS.ORDER_CONFIG);

        if (!configresponse.ok) throw new Error("Failed to fetch config");

        const data = await configresponse.json();
        setConfig(data);
        console.log("Fetched config:", data);
      } catch (error) {
        console.error("Error fetching config:", error);
      }
    };

    fetchConfig();
  }, []);

  const handleSubscription = async () => {
    try {
      if (!config) {
        console.error("Razorpay config not loaded");
        return;
      }

      if (!subscription) {
        console.error("No subscription data available to initiate payment");
        return;
      }

      // Build prefill only with valid values to avoid BAD_REQUEST payloads
      const prefill = {};
      const contact = localStorage.getItem("number");
      const email = localStorage.getItem("email");
      if (contact && typeof contact === "string" && contact.trim().length > 0) {
        prefill.contact = contact.trim();
      }
      if (email && typeof email === "string" && email.trim().length > 0) {
        prefill.email = email.trim();
      }

      const options = {
        key: config.key_id,
        subscription_id: subscription.razorpaySubscriptionId,
        name: "Tringbox",
        description: subscription.description || "Premium Monthly Subscription",
        image: "/images/splash.gif",
        theme: { color: "#6720A3" },
        ...(Object.keys(prefill).length > 0 ? { prefill } : {}),
        handler: async function () {
          const success = await verifyProfile();
          if (success) {
            showToast("Successfully Subscribed!", "success");
          } else {
            showToast(
              "Subscription succeeded, but profile update failed",
              "error"
            );
          }
          onClose();
        },
      };

      if (typeof window === "undefined" || !window.Razorpay) {
        console.error("Razorpay script not loaded");
        showToast("Payment is temporarily unavailable. Please retry.", "error");
        return;
      }

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        console.error("Payment failed:", response);
        alert("Payment failed. Please try again.");
      });

      rzp.open();
    } catch (err) {
      console.error("Subscription error:", err);
      alert("Error initiating subscription. Please try again.");
    }
  };

  if (!isOpen) return null;

  const isSubscribedLocal =
    (subscription?.status || "").toLowerCase() === "subscribed";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 py-4 px-3 animate-fadeIn">
      <div
        ref={modalRef}
        className={`bg-white rounded-2xl bottom-4 shadow-2xl max-w-[80vh] animate-slideUp transform relative ${
          !isSubscribedLocal
            ? "max-h-[75vh] lg:max-h-[90vh] overflow-y-auto"
            : ""
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-all duration-200 flex items-center justify-center"
        >
          ✕
        </button>

        {/* Header */}
        <div
          className="bg-gradient-to-r from-[#6720A3] to-[#8B5CF6] 
                px-6 pt-2 sm:pt-6 text-center text-white 
                relative overflow-hidden rounded-t-2xl 
                border-2 border-transparent border-t-purple-400 border-r-purple-400"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>

          <h1 className="font-bold text-xl sm:text-2xl mb-1 animate-fadeInUp">
            Subscription Plan
          </h1>

          <p className="text-white/90 text-sm animate-fadeInUp animation-delay-100">
            Unlock your potential
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-3 sm:p-8 space-y-6">
          {/* Info Section */}
          {!isSubscribedLocal ? (
            <div className="text-center animate-fadeInUp animation-delay-200">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Ready to Get Started?
              </h2>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Create and host unlimited events with our premium features. Grow
                your audience and manage everything with ease.
              </p>
              <a
                href="https://wa.me/917366052537"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#6720A3] font-medium text-sm hover:text-[#551e8a] transition-colors group"
              >
                <span>Need help? Contact us</span>
                <span className="group-hover:translate-x-0.5 transition-transform">
                  →
                </span>
              </a>
            </div>
          ) : (
            <></>
          )}

          {/* Subscription Card */}
          <div className="border-2 border-purple-200 rounded-xl p-6 bg-gradient-to-br from-purple to-white relative overflow-hidden animate-fadeInUp animation-delay-300 shadow-2xl shadow-purple-300">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-orange-400/20 rounded-full -translate-y-10 translate-x-10"></div>

            <div className="flex items-center justify-between md:mb-2 mb-1">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  {subscription?.name || ""}
                </h3>
                <p className="text-sm text-gray-600">
                  {subscription?.description || ""}
                </p>
              </div>
              <span className="text-2xl animate-bounce">🏅</span>
            </div>

            {/* Amount */}
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                {(() => {
                  const currency = (subscription?.currency || "INR").toUpperCase();
                  const symbolMap = {
                    INR: "₹",
                    USD: "$",
                    EUR: "€",
                    GBP: "£",
                    AED: "د.إ",
                    SAR: "﷼",
                    AUD: "A$",
                    CAD: "C$",
                    SGD: "S$",
                    JPY: "¥",
                  };
                  const symbol = symbolMap[currency] || currency + " ";
                  const amount = subscription ? subscription.amount / 100 : "—";
                  return `${symbol}${amount}`;
                })()}
              </span>
              <span className="text-gray-600 text-lg">
                /{subscription?.interval || "month"}
              </span>
            </div>

            {/* Features */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">
                What's Included:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {subscription?.features?.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 animate-fadeInUp"
                    style={{ animationDelay: `${400 + index * 100}ms` }}
                  >
                    <div className="w-5 h-5 bg-[#6720A3] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Subscribe Button */}
        <div
          className={`${
            !isSubscribedLocal ? "p-4 sm:p-6 pt-0" : "p-6 sm:p-8 pt-0"
          }`}
        >
          {isSubscribedLocal ? (
            <button
              onClick={() => setShowCancelModal(true)}
              className="w-full bg-gradient-to-r from-red-600 to-rose-500 hover:from-red-700 hover:to-rose-600 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform border border-red-400/50 hover:scale-[1.01]"
            >
              Cancel Subscription
            </button>
          ) : (
            <button
              onClick={handleSubscription}
              className="w-full group relative overflow-hidden bg-gradient-to-r from-[#6720A3] to-[#8B5CF6] hover:from-[#551e8a] hover:to-[#7C3AED] text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl animate-fadeInUp animation-delay-800"
            >
              {/* Shimmer Effect */}
              <div className="absolute inset-0 -top-2 flex h-full w-full justify-center blur-[12px] animate-shimmer-button">
                <div className="h-full w-8 bg-white/30 skew-x-12"></div>
              </div>

              {/* Button Content */}
              <div className="relative flex items-center justify-center gap-3">
                <span className="text-lg">Subscribe Now</span>
                <svg
                  className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </div>

              {/* Ripple Effect */}
              <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-active:opacity-100 group-active:animate-ping transition-opacity"></div>
            </button>
          )}
        </div>
      </div>
      {/* Cancellation Feedback Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center animate-fadeIn p-4">
          <div
            ref={cancelModalRef}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg md:max-w-2xl lg:max-w-3xl max-h-[90vh] flex flex-col border border-purple-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-rose-500 to-red-500 text-white rounded-t-2xl px-4 py-4 relative flex items-center justify-between">
              <h3 className="text-xl font-bold">Cancel Subscription</h3>
              <button
                onClick={() => setShowCancelModal(false)}
                className="w-8 h-8 rounded-full bg-white/20 backdrop-blur text-white hover:bg-white/30 transition-all duration-200 flex items-center justify-center"
                aria-label="Close cancel modal"
              >
                ✕
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-1">
              <div className="flex items-center gap-3 bg-red-100 border border-red-200 text-red-700 rounded-lg px-3 py-2 mb-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4m0 4h.01"
                  />
                </svg>
                <div className="min-w-0 flex-1 flex flex-col">
                  <span className="font-semibold text-sm leading-tight">
                    We're sorry to see you go!
                  </span>
                  <span className="text-xs text-red-600 leading-tight">
                    Your subscription will be cancelled immediately. Premium
                    features will no longer be accessible.
                  </span>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">
                  Help us improve
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Please tell us why you're cancelling your subscription:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    "Too expensive",
                    "Not using the features",
                    "Found a better alternative",
                    "Technical issues",
                    "Poor customer service",
                    "No longer need the service",
                    "Temporary financial constraints",
                    "Switching to free version",
                    "Other",
                  ].map((reason) => (
                    <label
                      key={reason}
                      className="flex items-center gap-3 cursor-pointer bg-gradient-to-r from-purple-50 to-white hover:from-purple-100/70 hover:to-white rounded-xl px-3 py-2 border border-purple-100"
                    >
                      <input
                        type="radio"
                        name="cancel-reason"
                        value={reason}
                        checked={selectedReason === reason}
                        onChange={() => {
                          setSelectedReason(reason);
                          setOtherReason("");
                        }}
                        className="rounded-full h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                      />
                      <span className="text-gray-700">{reason}</span>
                    </label>
                  ))}
                </div>

                {selectedReason === "Other" && (
                  <div className="mt-4">
                    <textarea
                      rows={3}
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      placeholder="Please specify"
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all duration-200 resize-none"
                    />
                  </div>
                )}

                <p className="text-sm text-gray-500 mt-4">
                  Your feedback helps us improve. We value your input and will
                  use it to enhance our service for future users.
                </p>
              </div>

              <div>
                <button
                  onClick={async () => {
                    const reason = (
                      selectedReason === "Other" ? otherReason : selectedReason
                    ).trim();
                    if (!reason) {
                      showToast("Please select or enter a reason", "error");
                      return;
                    }
                    setCancelReason(reason);
                    const success = await cancelSubscription(reason);
                    if (success) {
                      showToast(
                        "Subscription cancelled successfully",
                        "success"
                      );
                    } else {
                      showToast("Failed to cancel subscription", "error");
                    }
                    setShowCancelModal(false);
                  }}
                  className="w-full bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg shadow-red-200"
                >
                  Cancel Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(200%);
          }
        }

        @keyframes shimmer-button {
          0% {
            transform: translateX(-100%) skewX(-45deg);
          }
          100% {
            transform: translateX(300%) skewX(-45deg);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out both;
        }

        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }

        .animate-shimmer-button {
          animation: shimmer-button 2s ease-in-out infinite;
        }

        .animation-delay-100 {
          animation-delay: 100ms;
        }
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        .animation-delay-300 {
          animation-delay: 300ms;
        }
        .animation-delay-800 {
          animation-delay: 800ms;
        }
        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
        .animate-bounce {
          animation: bounce 2s infinite;
        }
      `}</style>
    </div>
  );
}
